// src/utils/tallyImport.ts
// ─────────────────────────────────────────────────────────────────────────────
// Maps a Tally.so webhook JSON payload to partial ClientData fields.
// ─────────────────────────────────────────────────────────────────────────────

import { ClientData } from '../types/ClientData';

export interface TallySubmission {
  formId: string;
  respondentId: string;
  submittedAt: string;
  fields: Array<{
    key: string;
    label: string;
    value: string | number | string[];
  }>;
}

export function mapTallyToClientData(submission: TallySubmission): Partial<ClientData> {
  const get = (label: string): string => {
    const field = submission.fields.find(f =>
      f.label.toLowerCase().includes(label.toLowerCase())
    );
    return field ? String(field.value || '') : '';
  };

  const getNum = (label: string): number => {
    const val = get(label);
    return parseFloat(val.replace(/[^0-9.]/g, '')) || 0;
  };

  const mapped: Partial<ClientData> = {};
  let count = 0;

  const trySet = <K extends keyof ClientData>(key: K, value: ClientData[K]) => {
    if (value && value !== 0 && value !== '') {
      (mapped as any)[key] = value;
      count++;
    }
  };

  trySet('clientName', (get('full name') || get('name')) as any);
  trySet('businessName', (get('business name') || get('company')) as any);
  trySet('clientEmail', (get('email') || get('email address')) as any);
  trySet('clientAge', (getNum('age') || undefined) as any);
  trySet('targetFreedomAge', (getNum('freedom age') || getNum('retirement age') || undefined) as any);
  trySet('salary', (getNum('salary') || getNum('annual salary') || undefined) as any);
  trySet('dividends', (getNum('dividend') || getNum('dividends') || undefined) as any);
  trySet('mortgageBalance', (getNum('mortgage balance') || getNum('outstanding mortgage') || undefined) as any);
  trySet('mortgageRate', (getNum('mortgage rate') || getNum('interest rate') || undefined) as any);
  trySet('pensionBalance', (getNum('pension') || getNum('pension fund') || undefined) as any);
  trySet('pensionCompanyFunded', (getNum('pension contribution') || getNum('monthly pension') || undefined) as any);
  trySet('isaBalance', (getNum('isa balance') || getNum('isa') || undefined) as any);
  trySet('investmentsBalance', (getNum('investments balance') || getNum('s&s isa') || getNum('isa balance') || getNum('isa') || undefined) as any);
  trySet('isaContribution', (getNum('isa contribution') || getNum('monthly isa') || undefined) as any);
  trySet('propertyValue', (getNum('property value') || getNum('home value') || undefined) as any);
  trySet('estimatedCompanyProfit', (getNum('company profit') || getNum('annual profit') || undefined) as any);
  trySet('businessRevenueMRR', (getNum('monthly revenue') || getNum('mrr') || (getNum('turnover') > 0 ? getNum('turnover') / 12 : 0) || undefined) as any);

  return { ...mapped, _tallyFieldCount: count } as any;
}
